import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import Modal from 'react-bootstrap-modal'
import DateInputWrapper from '../open-game-form/date-input-wrapper'
import SelectInputWrapper from '../open-game-form/select-input-wrapper'
import TextInputWrapper from '../open-game-form/text-input-wrapper'
import PriceInputWrapper from '../open-game-form/price-input-wrapper'
import Fields from '../open-game-form/fields'
import FlashAlert from '../flash-alert'
import PlayerSelector from '../master-selector'
import { ajaxGet, patchForm } from '../../ajax'
import CategorySelector from '../category-selector'
import moment from 'moment'

function GameForm({id, recurringId, onClose, onUpdate, onUpdateRecurring}) {
  const [payload, setPayload] = useState(null)
  const [baseField, setBaseField] = useState(null)
  const [master, setMaster] = useState(null)
  const [category, setCategory] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [alert, setAlert] = useState(null)
  const showTo = payload && payload.form.duration == 'custom'

  const { register, getValues, setValue, handleSubmit, formState: { errors } } = useForm()

  useEffect(() => {
    if (!payload) loadData({})
  }, [payload])

  const loadData = (params) => {
    if (recurringId) params['recurring_id'] = recurringId

    setIsLoading(true)
    ajaxGet(`/admins/reservations/${id}/edit.json?` + new URLSearchParams(params))
    .then(response => { if (response.ok) return response.json() })
    .then((json) => {
      setPayload(json)
      initForm(json.form)
      json.master && setMaster(json.master.value)
      setCategory(json.category_id)
      setIsLoading(false)
    })
  }

  const initForm = (data) => {
    for (const [key, value] of Object.entries(data)) { setValue(key, value) }
    setBaseField(data.field_id)
  }

  const prepareFormData = (data) => {
    let formData = new FormData()
    for (const key in data) {
      if (key == 'times') 
        data[key].forEach(v => formData.append('game[times][]', v))
      else
        formData.append(`game[${key}]`, data[key]) 
    }
    if (data.duration !== 'custom') formData.delete('game[duration_end_time]')
    if (recurringId) formData.append('reservation_id', id)
    formData.append('game[field_ids][]', baseField)
    if (category) formData.append('game[category_id]', category)
    if (master) formData.append('game[master_id]', master)
    return formData
  } 

  const submitUrl = () => {
    if (recurringId) 
      return `/admins/recurring_reservations/${recurringId}.json?`
    else
      return `/admins/reservations/${id}.json?`
  }

  const onSubmit = (data) => {
    setIsLoading(true)
    const formData = prepareFormData(data)

    patchForm(submitUrl(), formData)
    .then(response => { return response.json() })
    .then((json) => {
      setIsLoading(false)
      if (json.alert)
        setAlert(json.alert)
      else
      recurringId ? onUpdateRecurring() : onUpdate(json)
    })
  }

  const greaterThanFrom = (value) => {
    const from = getValues('times')[0]
    if (from >= value) return 'Must be > From'
    return true
  }

  const changePlayer = (data) => {
    setMaster(data && data.value)
  }

  const changeCategory = (value) => {
    setCategory(value)
  }

  const changeDate = (date) => {
    loadData({ date: date })
  }

  const changeGameOptions = (params) => {
    const formParams = {
      'game[date]': payload.date,
      'game[times][]': getValues('times')[0],
      'game[field_ids][]': baseField,
      'game[duration]': getValues('duration')
    }
    if (showTo) formParams['game[duration_end_time]'] = getValues('duration_end_time')
    loadData({...formParams, ...params})
  }

  const requiredMsg = 'Is required'
  
  if (!payload) return null
  const baseDate = payload.date

  return <Modal container={document.body} show={true} onHide={onClose}>
    <Modal.Header closeButton>
      <Modal.Title>Edit Reservation</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <div className={`game-form${isLoading ? ' isLoading' : ''}`}>
        { alert && <FlashAlert text={alert.text} type={alert.type} constant centred onHide={() => setAlert(null)} />}
        { isLoading && <div className='animated-spinner'></div> }
        <form className='form-horizontal' onSubmit={handleSubmit(onSubmit)}>
          <div className='row' style={{marginTop: '8px'}}>
            <div className='col-sm-9'>
              <PlayerSelector onChange={changePlayer} master={payload.master} />
            </div>
            <div className='col-sm-3'>
              <CategorySelector 
                categoryId={category}
                categories={payload.categories}
                onChange={changeCategory}
              />
            </div>
          </div><br />

          { !recurringId && 
            <div className='row'>
              <div className='col-md-9 col-sm-12'>
                <TextInputWrapper 
                  {...register('nickname')} 
                  placeholder='Add a Nickname'
                  onChange={(e) => setValue('nickname', e.target.value)}
                />
              </div>
            </div>
          }

          <div className='row'>
            <div className={payload.has_durations ? 'col-xs-6' : 'col-xs-9'}>
              <DateInputWrapper
                label='Date'
                onChange={(date) => changeGameOptions({'game[date]': (date || moment()).format('MM/DD/YYYY')})}
                date={moment(new Date(baseDate))}
              />
            </div>
            { payload.has_durations &&
              <div className='col-sm-2'>
                <SelectInputWrapper 
                  {...register('duration')}
                  label='Duration'
                  options={payload.durations}
                  onChange={(e) => changeGameOptions({'game[duration]': e.target.value})}
                />
              </div>
            }
            <div className='col-sm-2'>
              <SelectInputWrapper
                {...register('times[0]', { required: requiredMsg })}
                label='Time'
                selectClassName='time-select'
                options={payload.hours}
                error={errors.times && errors.times[0]}
                onChange={(e) => {
                  changeGameOptions({'game[times][]': e.target.value})
                  setValue('times[0]', e.target.value, { shouldValidate: true })
                }}
              />
            </div>
            { showTo && 
              <div className='col-sm-2'>
                <SelectInputWrapper
                  {...register('duration_end_time', { required: requiredMsg, validate: greaterThanFrom })} 
                  label='To'
                  selectClassName='time-select'
                  options={payload.hours}
                  error={errors.duration_end_time}
                  onChange={(e) => setValue('duration_end_time', e.target.value, { shouldValidate: true })}
                />
              </div>
            }
          </div>

          { recurringId && 
            <div className='row'>
              <div className='col-sm-2'>
                { payload.price_editable &&
                  <PriceInputWrapper
                    {...register('cost', { required: requiredMsg}) }
                    label='Cost'
                    error={errors.cost}
                    onChange={(e) => setValue('cost', e.target.value, { shouldValidate: true })}
                  />
                }
                { !payload.price_editable &&
                  <label className='control-label'>Price:&nbsp;
                    <span className='game-cost'>$ {parseFloat(payload.form.cost).toFixed(2)}</span>
                  </label>
                }
              </div>
            </div>
          }

          { payload.fields && baseField &&
            <div className='row'>
              <Fields
                selected={baseField}
                fields={payload.fields}
                fieldIndex={0}
                onChangeField={(value) => changeGameOptions({'game[field_ids][]': value})}
              />
            </div>
          }

          <Modal.Footer>
            { alert && <FlashAlert text={alert.text} type={alert.type} constant onHide={() => setAlert(null)} />}
            <input type='submit' name='commit' value='Update' className='btn btn-primary wider' />
          </Modal.Footer>
        </form>
      </div>
    </Modal.Body>
  </Modal>
}

GameForm.propTypes = {
  id: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired
}

export default GameForm
